import { IsPhaseAtLeast, type IOEPResource, type IOEPSeasonTimeline, type OEPPhase, type OEPReviewStatus } from 'agu_oep_core';
import { DayMonth, DaysUntil, FileTypeLabel, JoinAnd, Size } from '../shared/format';
import type { OEPTone } from '../presentation/presentation.vm';
import type { OEPFileInput } from '../shared/files';

/** The latest Uploaded draft of an assigned presentation (MJ: Files stores no size, so SizeBytes is usually null). */
export type OEPDraftFileInput = OEPFileInput;

const REVIEW_STATUSES: readonly OEPReviewStatus[] = ['NotStarted', 'InProgress', 'Completed', 'Submitted'];
/** Narrow a stored Review.Status to the union (null for anything else). */
export const AsReviewStatus = (v: string | null): OEPReviewStatus | null => REVIEW_STATUSES.find((s) => s === v) ?? null;

export interface OEPAssignmentInput {
    AssignmentID: string;
    Title: string;
    SectionName: string;
    Format: string | null;
    Code: string | null;
    /** The presenter's Participant.CareerStage (readable by assigned reviewers, T5) — the prototype's "[Career level]". */
    CareerStage: string | null;
    Draft: OEPDraftFileInput | null;
    /** The reviewer's own Review row for this assignment; null before the first save. */
    Review: { Status: OEPReviewStatus; CompletedDate: Date | null } | null;
}

export interface ReviewsInputs {
    Phase: OEPPhase;
    Timeline: IOEPSeasonTimeline;
    Now: Date;
    Assignments: OEPAssignmentInput[];
    /** Season.Resources.reviewer. */
    Resources: IOEPResource[];
    /** True when at least one presenter sent this reviewer a thank-you note. */
    ReceivedThanks: boolean;
}

export interface OEPAssignmentRowVM {
    AssignmentID: string;
    Header: string;
    Title: string;
    Sub: string | null;
    Status: string;
    Tone: OEPTone;
    Due: string | null;
    DueTone: 'ok' | 'warn' | 'muted';
    Cta: string;
    CtaEnabled: boolean;
    Draft: { FileID: string; Label: string } | null;
}

export interface ReviewsVM {
    Intro: string;
    /**
     * Before `Matched` (Setup … DraftClosed) RLS hides every assignment (2026-10-19 §5.2), so instead of the
     * "matched to you" callout the page says when matching happens. Null from `Matched` on.
     */
    BeforeMatching: string | null;
    Pending: { Title: string; Text: string; Links: { Label: string; Url: string }[] } | null;
    Guidance: { Title: string; Text: string } | null;
    Wrap: { Title: string; Text: string } | null;
    Rows: OEPAssignmentRowVM[];
}

type Mode = 'pre' | 'open' | 'closed';

/**
 * logic.js:274 mode: sum → pre, nov → open, later → closed; mapped onto phases (task 26 brief). Every phase before
 * Reviewing is 'pre' — with drafts before matching (2026-10-19 §5.1) that includes DraftUpload and DraftClosed.
 */
function modeOf(phase: OEPPhase): Mode {
    if (phase === 'Reviewing') return 'open';
    return IsPhaseAtLeast(phase, 'Reviewing') ? 'closed' : 'pre';
}

/** A review counts as submitted once the reviewer submitted it (Submitted) or staff released it (Completed). */
export function IsReviewSubmitted(r: { Status: OEPReviewStatus } | null): boolean {
    return r !== null && (r.Status === 'Submitted' || r.Status === 'Completed');
}

/**
 * "Section · Format · CareerStage · Session: Code" (logic.js:277); missing parts are left out. The code is AGU's
 * session code (`H041`, Presentation.PresentationCode as the roster loads it), so it is labelled Session.
 */
export function AssignmentHeader(a: Pick<OEPAssignmentInput, 'SectionName' | 'Format' | 'CareerStage' | 'Code'>): string {
    const parts = [a.SectionName, a.Format, a.CareerStage].filter((x): x is string => x !== null && x.length > 0);
    if (a.Code) parts.push(`Session: ${a.Code}`);
    return parts.join(' · ');
}

/** "PDF, 8.4 MB" (logic.js:264 `file`); just the type when the size is unknown. */
export function DraftFileKind(d: OEPDraftFileInput): string {
    return [FileTypeLabel(d.ContentType, d.Name), Size(d.SizeBytes)].filter((x) => x.length > 0).join(', ');
}

function row(i: ReviewsInputs, a: OEPAssignmentInput): OEPAssignmentRowVM {
    const d = i.Timeline.Dates;
    const mode = modeOf(i.Phase);
    const done = IsReviewSubmitted(a.Review);
    const submittedOn = `Submitted ${a.Review?.CompletedDate ? DayMonth(a.Review.CompletedDate) : d.FeedbackDue.Label}`;
    const base = {
        AssignmentID: a.AssignmentID,
        Header: AssignmentHeader(a),
        Title: a.Title,
        // logic.js:287
        Sub: a.Draft ? null : 'Presentation matched to you based on your section preferences.',
        // markup.html:541 "Download draft ({{ a.file }})"
        Draft: a.Draft ? { FileID: a.Draft.FileID, Label: `Download draft (${DraftFileKind(a.Draft)})` } : null,
    };
    // logic.js:278-286
    if (mode === 'pre') {
        return { ...base, Status: 'Matched', Tone: 'neutral', Due: `Feedback window: ${d.ReviewWindow.Label}`, DueTone: 'muted', Cta: `Opens ${d.ReviewOpens.Label}`, CtaEnabled: false };
    }
    if (done) return { ...base, Status: 'Submitted', Tone: 'ok', Due: submittedOn, DueTone: 'ok', Cta: 'View what you wrote', CtaEnabled: true };
    if (mode === 'open') {
        const left = DaysUntil(d.FeedbackDue.On, i.Now);
        return {
            ...base,
            Status: a.Review?.Status === 'InProgress' ? 'In progress' : 'Not started',
            Tone: 'warn',
            Due: `Feedback due ${d.FeedbackDue.Label} · ${left} ${left === 1 ? 'day' : 'days'} left`,
            DueTone: 'warn',
            Cta: 'Give feedback',
            CtaEnabled: true,
        };
    }
    return { ...base, Status: 'Not submitted', Tone: 'hot', Due: null, DueTone: 'muted', Cta: 'View', CtaEnabled: true };
}

/** Assigned sections in first-seen order. */
function sections(a: readonly OEPAssignmentInput[]): string[] {
    return [...new Set(a.map((x) => x.SectionName).filter((n) => n.length > 0))];
}

/** markup.html:493-554 / logic.js:270-292, 636-641 as one pure function. Every string cites its prototype line. */
export function BuildReviewsVM(i: ReviewsInputs): ReviewsVM {
    const d = i.Timeline.Dates;
    // Keyed on the mode, not the season stage: with drafts before matching (2026-10-19 §5.1) the nov stage starts
    // at DraftUpload, before any assignment is visible to the reviewer, so the pre-window copy holds until Reviewing.
    const mode = modeOf(i.Phase);
    const matched = IsPhaseAtLeast(i.Phase, 'Matched');
    // logic.js:638-641 reviewIntro
    const intro = mode === 'pre'
        ? `You will be able to view and provide feedback on the draft presentation assigned to you beginning ${d.ReviewOpens.Label}. Please submit your feedback before the deadline: ${d.FeedbackDue.Label} at 11:59 p.m. Eastern.`
        : mode === 'open'
            ? `You volunteered to review in ${JoinAnd(sections(i.Assignments))}. The feedback window is ${d.ReviewWindow.Label}, so presenters can revise before the meeting.`
            : 'Your reviews are complete. Here is what came back.';
    return {
        Intro: intro,
        BeforeMatching: matched ? null
            : `Reviewers are matched to presentations after the draft deadline (${d.DraftDue.Label}); your matches appear here ${d.MatchingRun.Label}.`,
        // markup.html:497-507 (the calendar link has no backing feature and is omitted). Only once matched: before
        // Matched no assignment is visible, so "the presentations below" would point at nothing.
        Pending: mode === 'pre' && matched ? {
            Title: 'Presentations for Feedback',
            Text: `The presentations below have been matched to you based on your section preferences. Please provide feedback during the review window: ${d.ReviewWindow.Label}.`,
            Links: i.Resources.slice(0, 2).map((r) => ({ Label: r.Kind ? `${r.Title} (${r.Kind})` : r.Title, Url: r.Url })),
        } : null,
        // markup.html:522-527
        Guidance: i.Phase === 'Reviewing' ? {
            Title: 'Giving effective, supportive feedback',
            Text: 'Providing effective, supportive feedback for emerging science research is important to help presenters grow and develop in their research. Be specific about what worked and what one or two changes would help most. Emerging presenters are seeking feedback that not only helps them grow as a scientist, but also encourages them to develop and continue to pursue the science that they are interested in.',
        } : null,
        // markup.html:510-519 (the quoted presenter line is omitted: note text is not surfaced here)
        Wrap: i.Phase === 'Complete' ? {
            Title: 'Your season is complete. Thank you',
            Text: `AGU will email your acknowledgement.${i.ReceivedThanks ? ' One of your presenters wrote back.' : ''}`,
        } : null,
        Rows: i.Assignments.map((a) => row(i, a)),
    };
}
